import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Grid, TextField, Button, Box,
    Alert, CircularProgress, Divider, Chip, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip
} from '@mui/material';
import {
    Save as SaveIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Wifi as WifiIcon,
    WifiOff as WifiOffIcon,
    Router as RouterIcon
} from '@mui/icons-material';
import axios from 'axios';

const MqttSettingsPage = () => {
    const [config, setConfig] = useState(null);
    const [gateways, setGateways] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    
    const [formData, setFormData] = useState({
        mqtt_broker: '',
        mqtt_port: 1883,
        mqtt_username: '',
        mqtt_password: '',
        mqtt_client_id: '',
        mqtt_topic_prefix: '',
        mqtt_base_topic: '',
        use_ssl: false,
        default_qos: 1
    });

    useEffect(() => {
        loadConfig();
        loadGateways();
        
        // Aggiorna gateways ogni 30 secondi
        const interval = setInterval(loadGateways, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadConfig = async () => {
        try {
            const response = await axios.get('/api/mqtt/config');
            if (response.data.success) {
                setConfig(response.data.config);
                setFormData({
                    mqtt_broker: response.data.config.mqtt_broker,
                    mqtt_port: response.data.config.mqtt_port,
                    mqtt_username: response.data.config.mqtt_username,
                    mqtt_password: '', // Non mostrare password
                    mqtt_client_id: response.data.config.mqtt_client_id,
                    mqtt_topic_prefix: response.data.config.mqtt_topic_prefix,
                    mqtt_base_topic: response.data.config.mqtt_base_topic,
                    use_ssl: response.data.config.use_ssl,
                    default_qos: response.data.config.default_qos
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Errore caricamento configurazione'
            });
        } finally {
            setLoading(false);
        }
    };

    const loadGateways = async () => {
        try {
            const response = await axios.get('/api/mqtt/gateways');
            if (response.data.success) {
                setGateways(response.data.gateways);
            }
        } catch (error) {
            console.error('Errore caricamento gateway:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });
        
        try {
            const response = await axios.put('/api/mqtt/config', formData);
            if (response.data.success) {
                setMessage({
                    type: 'success',
                    text: 'Configurazione salvata! Riavvia il server per applicare le modifiche.'
                });
                loadConfig();
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Errore salvataggio configurazione'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        setTesting(true);
        setMessage({ type: '', text: '' });
        
        try {
            const response = await axios.post('/api/mqtt/test-connection', {
                mqtt_broker: formData.mqtt_broker,
                mqtt_port: formData.mqtt_port,
                mqtt_username: formData.mqtt_username,
                mqtt_password: formData.mqtt_password || config?.mqtt_password
            });
            
            if (response.data.success) {
                setMessage({
                    type: 'success',
                    text: 'Connessione MQTT riuscita! ✅'
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Test connessione fallito'
            });
        } finally {
            setTesting(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'online': return 'success';
            case 'offline': return 'error';
            case 'warning': return 'warning';
            default: return 'default';
        }
    };

    const getHealthStatusIcon = (healthStatus) => {
        switch (healthStatus) {
            case 'online': return <WifiIcon color="success" />;
            case 'offline': return <WifiOffIcon color="error" />;
            case 'warning': return <WifiIcon color="warning" />;
            default: return <WifiOffIcon color="disabled" />;
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                <RouterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Impostazioni MQTT
            </Typography>
            
            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                </Alert>
            )}

            {/* Configurazione MQTT */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Configurazione Broker MQTT
                </Typography>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            label="Indirizzo Broker"
                            name="mqtt_broker"
                            value={formData.mqtt_broker}
                            onChange={handleChange}
                            placeholder="195.231.61.29"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Porta"
                            name="mqtt_port"
                            type="number"
                            value={formData.mqtt_port}
                            onChange={handleChange}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Username"
                            name="mqtt_username"
                            value={formData.mqtt_username}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Password"
                            name="mqtt_password"
                            type="password"
                            value={formData.mqtt_password}
                            onChange={handleChange}
                            placeholder="Lascia vuoto per non modificare"
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Client ID"
                            name="mqtt_client_id"
                            value={formData.mqtt_client_id}
                            onChange={handleChange}
                            helperText="ID univoco per questo client MQTT"
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Topic Base"
                            name="mqtt_base_topic"
                            value={formData.mqtt_base_topic}
                            onChange={handleChange}
                            placeholder="CASAFRANCESCANA"
                            helperText="Prefisso principale per tutti i topic"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Topic Prefix Completo"
                            name="mqtt_topic_prefix"
                            value={formData.mqtt_topic_prefix}
                            onChange={handleChange}
                            placeholder="CASAFRANCESCANA/ROOM001"
                            helperText="Topic completo per i gateway"
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        Salva Configurazione
                    </Button>
                    
                    <Button
                       
                        startIcon={testing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                        onClick={handleTestConnection}
                        disabled={testing}
                    >
                        Test Connessione
                    </Button>
                </Box>
            </Paper>

            {/* Stato Connessione */}
            {config && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Stato Connessione
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Stato:
                                    </Typography>
                                    <Chip
                                        label={config.connection_status}
                                        color={getStatusColor(config.connection_status)}
                                        size="small"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Ultima connessione: {config.last_connection ? new Date(config.last_connection).toLocaleString('it-IT') : 'Mai'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Attivo: {config.is_active ? '✅ Sì' : '❌ No'}
                                </Typography>
                            </Grid>
                        </Grid>
                        
                        {config.last_error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Ultimo errore: {config.last_error}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Gateways BLE Registrati */}
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Gateway BLE Registrati ({gateways.length})
                    </Typography>
                    <IconButton onClick={loadGateways} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Box>
                
                {gateways.length === 0 ? (
                    <Alert severity="info">
                        Nessun gateway registrato. I gateway si registreranno automaticamente al primo avvio.
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Stato</TableCell>
                                    <TableCell>Device ID</TableCell>
                                    <TableCell>MAC Address</TableCell>
                                    <TableCell>Stanza</TableCell>
                                    <TableCell>IP Address</TableCell>
                                    <TableCell>MQTT Topic</TableCell>
                                    <TableCell>Ultimo Heartbeat</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {gateways.map((gateway) => (
                                    <TableRow key={gateway.gateway_id}>
                                        <TableCell>
                                            <Tooltip title={gateway.health_status}>
                                                {getHealthStatusIcon(gateway.health_status)}
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {gateway.device_id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {gateway.mac_address}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {gateway.room_name || gateway.room_id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {gateway.ip_address || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                {gateway.mqtt_prefix}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {gateway.last_seen 
                                                    ? new Date(gateway.last_seen).toLocaleString('it-IT')
                                                    : 'Mai'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Container>
    );
};

export default MqttSettingsPage;
